import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { NOTE_TYPES, LEAD_STATUSES } from '@/lib/constants';

/**
 * Reusable Notes panel — display & add visit/lead/status notes
 * for a given property or tenant. Available to admin, consultant, agent.
 * Tenants/owners have NO write access (owners can view only).
 */
export function NotesPanel({ propertyId, tenantId, className = '' }) {
  const { user } = useAuth();
  const canWrite = user && ['admin', 'consultant', 'agent'].includes(user.role);
  const canView = user && ['admin', 'consultant', 'agent', 'owner'].includes(user.role);

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ note_type: 'visit', body: '', lead_status: '' });

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {};
      if (propertyId) params.property_id = propertyId;
      if (tenantId) params.tenant_id = tenantId;
      const r = await api.get('/notes', { params });
      setNotes(r.data);
    } catch (e) { /* no toast — empty UI is fine */ }
    setLoading(false);
  }, [propertyId, tenantId, canView]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.body.trim()) return toast.error('Add some text to the note');
    try {
      await api.post('/notes', {
        note_type: form.note_type,
        body: form.body.trim(),
        property_id: propertyId || null,
        tenant_id: tenantId || null,
        lead_status: form.lead_status || null,
      });
      toast.success('Note added');
      setForm({ note_type: 'visit', body: '', lead_status: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/notes/${id}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (!canView) return null;

  return (
    <Card className={`p-4 space-y-4 ${className}`} data-testid="notes-panel">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-muted-foreground" />
        <div className="font-semibold text-sm">Visit, Lead &amp; Status Notes</div>
        <Badge variant="outline" className="text-[10px] ml-auto">{notes.length}</Badge>
      </div>

      {canWrite && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 border-b pb-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v })}>
              <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{NOTE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {(form.note_type === 'lead' || form.note_type === 'followup') && (
            <div>
              <Label className="text-xs">Lead status</Label>
              <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="sm:col-span-3">
            <Label className="text-xs">Note</Label>
            <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                      rows={3} placeholder="Visited prospective tenant. Interested in 2BHK with sea view. Will revert by Friday."
                      data-testid="note-body-input" />
          </div>
          <div className="sm:col-span-3 flex justify-end">
            <Button size="sm" onClick={add} data-testid="note-add-button"><Plus className="h-3.5 w-3.5 mr-1" /> Add note</Button>
          </div>
        </div>
      )}

      <div className="space-y-2.5 max-h-[420px] overflow-y-auto">
        {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
        {!loading && notes.length === 0 && (
          <div className="text-xs text-muted-foreground py-6 text-center">No notes yet for this {propertyId ? 'property' : 'tenant'}.</div>
        )}
        {notes.map((n) => (
          <div key={n.id} className="rounded-lg border bg-card p-3" data-testid={`note-row-${n.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-[10px] capitalize">{n.note_type}</Badge>
                {n.lead_status && <Badge variant="outline" className="text-[10px] capitalize bg-amber-50 text-amber-700 border-amber-200">{n.lead_status.replace(/_/g, ' ')}</Badge>}
                <span className="text-[11px] text-muted-foreground">by {n.author_name} <span className="capitalize">({n.author_role})</span></span>
              </div>
              {(user.role === 'admin' || n.author_id === user.id) && (
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(n.id)} aria-label="Delete note">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
            <div className="text-sm mt-1.5 whitespace-pre-wrap break-words">{n.body}</div>
            <div className="text-[11px] text-muted-foreground mt-1.5">{new Date(n.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
